/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * A linked AI account for a user. The identity is (provider, email): the same email registered on two providers is two distinct accounts.
 */
export type UserAccount = {
  /**
   * 'team' (enterprise) or 'personal' (individual); empty when not yet classified
   */
  accountType?: string | undefined;
  /**
   * Email associated with the account; may differ from the user's work email for personal accounts
   */
  email?: string | undefined;
  /**
   * Provider org id for this account; the per-account discriminator used to scope telemetry to this one account
   */
  externalOrgId?: string | undefined;
  /**
   * Account record id (user_accounts.id); used to scope chat/session views to this account
   */
  id?: string | undefined;
  /**
   * Latest activity timestamp for this account in Unix nanoseconds
   */
  lastSeenUnixNano?: string | undefined;
  /**
   * AI provider the account belongs to ('anthropic', 'openai', 'cursor')
   */
  provider: string;
  /**
   * Speakeasy user id of the directory owner of this account; the authoritative link between account-email-keyed usage and the org member
   */
  userId?: string | undefined;
};

/** @internal */
export const UserAccount$inboundSchema: z.ZodMiniType<UserAccount, unknown> = z
  .pipe(
    z.object({
      account_type: z.optional(z.string()),
      email: z.optional(z.string()),
      external_org_id: z.optional(z.string()),
      id: z.optional(z.string()),
      last_seen_unix_nano: z.optional(z.string()),
      provider: z.string(),
      user_id: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "account_type": "accountType",
        "external_org_id": "externalOrgId",
        "last_seen_unix_nano": "lastSeenUnixNano",
        "user_id": "userId",
      });
    }),
  );

export function userAccountFromJSON(
  jsonString: string,
): SafeParseResult<UserAccount, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => UserAccount$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'UserAccount' from JSON`,
  );
}
